import { useEffect, useRef, useState } from "react";
import { apiUrl } from "../../api/client";
import { downloadFile } from "../../api/download";
import { NAVY } from "./primitives";
import { IntegratedDeliverables } from "./integrated-deliverables";
import { ReportPack } from "./report-pack";
import { ReportVersions } from "./report-versions";

/**
 * The reports of the latest run (Step 7a). Each opens as a web page or downloads as a PDF rendered on
 * the server, and as editable Word. The Migration programme is
 * migration planning, optional and listed apart. The lens decides which report is shown first:
 * Executive → Simplified, Detailed Risk Assessment → Detailed, Migration programme → Migration programme.
 */
export type ReportId = "risk-assessment-simplified" | "risk-assessment-detailed" | "programme";

export const REPORTS: Array<{ id: ReportId; label: string; detail: string; word?: boolean; programmeOption?: boolean; planning?: boolean }> = [
  { id: "risk-assessment-simplified", label: "Risk Assessment Report — Simplified", detail: "For leadership: the main answer, the evidence, readiness and next steps", word: true },
  { id: "risk-assessment-detailed", label: "Risk Assessment Report — Detailed", detail: "Every finding, dimension, time test, mandate, vendor and readiness item; evidence and asset cards", word: true, programmeOption: true },
  { id: "programme", label: "Migration programme", detail: "Waves, asset timeline, blockers, actions and owners, budget", word: true, planning: true },
];

const query = (id: ReportId, withProgramme: boolean) => (withProgramme && REPORTS.find((r) => r.id === id)?.programmeOption ? "?include_programme=1" : "");

const download = (projectId: string, id: ReportId, ext: "pdf" | "docx", withProgramme: boolean) =>
  downloadFile(`/projects/${projectId}/reports/${id}.${ext}${query(id, withProgramme)}`, `${id}.${ext}`).catch((e: unknown) => {
    const m = e instanceof Error ? e.message : String(e);
    throw new Error(m.startsWith("Download failed") ? m.replace("Download", ext === "pdf" ? "PDF download" : "Word download") : m);
  });

export function ReportsMenu({ projectId, primary }: { projectId: string; primary: ReportId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [withProgramme, setWithProgramme] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const main = REPORTS.find((r) => r.id === primary)!;
  const get = async (id: ReportId, ext: "pdf" | "docx" = "pdf") => {
    setError(null); setBusy(ext === "pdf" ? id : `${id}.${ext}`);
    try { await download(projectId, id, ext, withProgramme); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const btn = "text-xs rounded-sm px-3 py-1.5 whitespace-nowrap";
  const small = "text-[11px] text-ink-700 hover:text-accent border border-ink-200 rounded-sm px-2 py-1 disabled:opacity-50";
  const row = (r: (typeof REPORTS)[number]) => (
    <div key={r.id} className="px-2 py-2 border-b border-ink-100 last:border-b-0" data-testid={`reports-list-${r.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-ink-900">{r.label}</div>
          <div className="text-[11px] text-ink-500">{r.detail}</div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <a href={apiUrl(`/projects/${projectId}/reports/${r.id}.html${query(r.id, withProgramme)}`)} target="_blank" rel="noopener noreferrer" data-testid={`reports-list-${r.id}-open`} className={small}>Open</a>
          <button type="button" onClick={() => get(r.id)} disabled={busy !== null} data-testid={`reports-list-${r.id}-pdf`} className={small}>{busy === r.id ? "Preparing…" : "PDF"}</button>
          {r.word && (
            <button type="button" onClick={() => get(r.id, "docx")} disabled={busy !== null} data-testid={`reports-list-${r.id}-docx`} className={small}>{busy === `${r.id}.docx` ? "Preparing…" : "Word"}</button>
          )}
        </div>
      </div>
      {r.programmeOption && (
        <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-ink-600">
          <input type="checkbox" checked={withProgramme} onChange={(e) => setWithProgramme(e.target.checked)} data-testid="reports-include-programme" />
          Include migration programme <span className="text-ink-400">(appended as Part 2 to the web page, PDF and Word)</span>
        </label>
      )}
    </div>
  );
  return (
    <div className="relative flex flex-col items-end gap-1" ref={ref} data-testid="reports-menu">
      <div className="flex items-center gap-2">
        <a href={apiUrl(`/projects/${projectId}/reports/${main.id}.html`)} target="_blank" rel="noopener noreferrer" data-testid={`report-${main.id}`}
          className={`${btn} text-white`} style={{ background: NAVY }} title={main.detail}>{main.label}</a>
        <button type="button" onClick={() => get(main.id)} disabled={busy !== null} data-testid={`report-${main.id}-pdf`}
          className={`${btn} text-ink-700 hover:text-accent border border-ink-200 disabled:opacity-50`} title={`Download the ${main.label} as a PDF`}>
          {busy === main.id ? "Preparing PDF…" : "Download PDF"}
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="reports-all"
          className={`${btn} text-ink-700 hover:text-accent border border-ink-200`}>All reports ▾</button>
      </div>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-[28rem] bg-white border border-ink-200 rounded-sm shadow-lg p-2" role="menu" data-testid="reports-list">
          {REPORTS.filter((r) => !r.planning).map(row)}
          <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500" data-testid="reports-planning-heading">Migration planning (optional)</div>
          {REPORTS.filter((r) => r.planning).map(row)}
          <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500" data-testid="reports-pack-heading">Report pack: UCDI, QRAM and QS-ETSF</div>
          <ReportPack projectId={projectId} />
          <div className="px-2"><IntegratedDeliverables projectId={projectId} /></div>
          <ReportVersions projectId={projectId} />
        </div>
      )}
      {error && <p className="text-[11px] text-red-700 max-w-sm text-right" role="alert" data-testid="reports-error">{error}</p>}
    </div>
  );
}
